import { Alert, CircularProgress, Modal, Snackbar, Box, FormControl, InputLabel, Dialog, DialogContent, DialogContentText, DialogActions, DialogTitle } from "@mui/material";
import { useEffect, useState } from "react";
import { BlueHoverButton } from "../../../components/Inputs/BlueHoverButton";
import { style as modalStyle } from "../../../components/Loading/Loading";
import { ErrorHandling } from "../../../components/Errors/Errors";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { logout } from "../../../state/slices/userSlice";
import { WhiteInput } from "../../../components/Inputs/InputText";
import servSolicitacaoAcesso from "../../../services/login/servSolicitacaoAcesso";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import ptBR from "date-fns/locale/pt-BR";
import { parse } from "date-fns";
import { Controller, useForm } from "react-hook-form";
import IndividualFileInput from "../../../components/Inputs/IndividualFileInput";
import { downloadConteudoBase64 } from "../../../infra/utils/DownloadFile";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { HelperText } from "../../../components/Errors/HelperText";

const schema = yup.object().shape({
    dataInicioInstalacao: yup.date().typeError("Valor inválido. Este campo é obrigatório").required("Valor inválido. Este campo é obrigatório")
});

const InicioInstalacao = ({ setOpenPopup, recordForEdit, pesquisar }) => {
    const { register, getValues, setValue, handleSubmit, control, formState: { errors } } = useForm({ resolver: yupResolver(schema) });
    const [isLoading, setIsLoading] = useState(false);
    const [openErrorWindow, setOpenErrorWindow] = useState(false);
    const [openSuccessWindow, setOpenSuccessWindow] = useState(false);
    const [errorMessage, setErrorMessage] = useState("");
    const [successMessage, setSuccessMessage] = useState("");
    const [inicioInstalacao, setInicioInstalacao] = useState(null);
    const [temArquivoInformado, setTemArquivoInformado] = useState(false);
    const [isEditavel, setIsEditavel] = useState(true);
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const [openDialog, setOpenDialog] = useState(false);

    useEffect(() => {
        const recuperarInicioInstalacao = async () => {
            try {
                setIsLoading(true);
                
                let dados = await servSolicitacaoAcesso.RecuperarInicioDaInstalacao(recordForEdit?.row?.Id || recordForEdit?.Id);
                
                setIsLoading(false);

                if(dados.Content.Id === 0) {
                    informarValoresIniciais(dados.Content);
                    setInicioInstalacao(dados.Content);
                } else {
                    formatarValores(dados.Content);
                    setInicioInstalacao(dados.Content);
                    setIsEditavel(false);
                }
            } catch (err) {
                setIsLoading(false);
                setErrorMessage(err.Message);
                setOpenErrorWindow(true);
            }
        }

        recuperarInicioInstalacao();
    }, []);

    const formatarValores = (content) => {
        content.CodigoSolicitacaoDeAcesso = recordForEdit?.row?.Id || recordForEdit?.Id;
        setValue("dataInicioInstalacao", parse(content.DataInicioInstalacao, 'dd/MM/yyyy HH:mm:ss', new Date()));
    }

    const informarValoresIniciais = (content) => {
        let dataInicial = new Date();

        dataInicial.setHours(0);
        dataInicial.setMinutes(0);
        dataInicial.setSeconds(0);
        dataInicial.setMilliseconds(0);
        
        content.CodigoSolicitacaoDeAcesso = recordForEdit?.row?.Id || recordForEdit?.Id;
        setValue("dataInicioInstalacao", dataInicial);
    }

    const informarInicioDeInstalacao = handleSubmit( async () => {
        if (recordForEdit === null) {
            return;
        }

        try {
            setIsLoading(true);

            const formData = createFormData(inicioInstalacao);

            await servSolicitacaoAcesso.InformarInicioInstalacao(formData);
            
            setOpenSuccessWindow(true);
            setSuccessMessage("Início de instalação informado com sucesso.");
            setIsLoading(false);

            setOpenDialog(false);

            setTimeout(() => {
                setOpenPopup(false);
                pesquisar();
            }, [1400]);
        } catch (err) {
            setIsLoading(false);

            return ErrorHandling(
                err?.request.status, 
                err?.message, 
                setOpenErrorWindow, 
                setErrorMessage, 
                dispatch, 
                logout, 
                navigate,
                err?.response?.data?.Message
            );
        }  
    })

    const createFormData = (inicioInstalacao) => {
        const formData = new FormData();

        formData.append("CodigoSolicitacaoAcesso", inicioInstalacao.CodigoSolicitacaoDeAcesso);
        formData.append("DataInicioInstalacao", getValues('dataInicioInstalacao').toJSON());
        formData.append("Arquivo", inicioInstalacao.Arquivo ?? null);
        formData.append("NomeArquivo", inicioInstalacao.NomeArquivo ?? " ");
        formData.append("ExtensaoArquivo", inicioInstalacao.ExtensaoDocumento ?? " ");
        formData.append("TamanhoKbArquivo", inicioInstalacao.TamanhoKbArquivo ?? 0);
        formData.append("CodigoDocumento", inicioInstalacao.CodigoDocumento ?? 0);

        return formData;
    }

    const handleUploadCartaInicioInstalacao = (event) => {
        let target = event.target;
        let file = target.files ? target.files[0] : null;

        if (file === null) {
            handleCloseError(true);
            return setErrorMessage("Selecione um arquivo");
        }

        let nomeArquivoSeparadoPorPonto = file.name.split('.');
        const extensaoArquivo = nomeArquivoSeparadoPorPonto.pop();
        const nomeArquivo = nomeArquivoSeparadoPorPonto.join('.');
        const tamanhoKbArquivo = file.size;

        inicioInstalacao.NomeArquivo = nomeArquivo;
        inicioInstalacao.ExtensaoDocumento = extensaoArquivo;
        inicioInstalacao.TamanhoKbArquivo = tamanhoKbArquivo;
        inicioInstalacao.Arquivo = file;

        setTemArquivoInformado(true);
    }

    const realizarDownloadDocumento = async (event) => {
        event.preventDefault();

        try {
            setIsLoading(true);

            if(inicioInstalacao.CodigoDocumento === 0 || inicioInstalacao.CodigoDocumento === null) {
                setOpenErrorWindow(true);
                setErrorMessage("Não há carta de instalação informada.");
                setIsLoading(false);
                return;
            }

            const response = await servSolicitacaoAcesso.VisualizarCartaDeInstalacao(inicioInstalacao?.CodigoSolicitacaoDeAcesso);
            
            downloadConteudoBase64(response.Content.Nome + "." + response.Content.Extensao, response.Content.Arquivo);

            setIsLoading(false);
        } catch (err) {
            setIsLoading(false);

            return ErrorHandling(
                err?.request.status, 
                err?.message, 
                setOpenErrorWindow, 
                setErrorMessage, 
                dispatch, 
                logout, 
                navigate,
                err?.response?.data?.Message
            );
        }
    }

    const handleCloseSuccess = (event, reason) => {
        if (reason  === 'clickaway') {
            setOpenSuccessWindow(false);
        }

        setOpenSuccessWindow(false);
    }

    const handleCloseError = (event, reason) => {
        if (reason  === 'clickaway') {
            setOpenErrorWindow(false);
        }

        setOpenErrorWindow(false);
    }

    const handleClickOpenDialog = (event, reason) => {
        if (reason  === 'clickaway') {
            setOpenDialog(true);
        }

        setOpenDialog(true);
    }

    const handleCloseDialog = (event, reason) => {
        if (reason  === 'clickaway') {
            setOpenDialog(false);
        }

        setOpenDialog(false);
    }

    return (
        <div style={{ minWidth: 540, minHeight: 325, width: "100%", height: "20%" }}>
            <div style={{ padding: "20px", minHeight: 250 }}>


                <FormControl variant="outlined" style={{ margin: '10px 0px 20px 35px', width: "50%" }}>
                    <InputLabel shrink style={{ color: "#B8B8B8" }} >
                        Data de início da instalação
                    </InputLabel>
                    <Controller 
                        name="dataInicioInstalacao"
                        control={control}
                        render={({ field: { onChange, value } }) => (
                            <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={ptBR}>
                                <DatePicker
                                    fullWidth
                                    inputFormat="dd/MM/yyyy"
                                    value={value}
                                    error={!!errors.dataInicioInstalacao}
                                    readOnly={!isEditavel}
                                    onChange={onChange}
                                    renderInput={({ inputRef, inputProps, InputProps }) => (
                                        <>
                                            <WhiteInput ref={inputRef}  {...inputProps} sx={{color: "#fff"}} />
                                            <span style={{ position: "absolute", top: 38, right: 15, color: "white", svg: "white", input: "white", label: "white" }}>
                                                {InputProps?.endAdornment}
                                            </span>
                                        </>
                                    )}
                                />
                            </LocalizationProvider>
                        )}
                    />
                    { errors?.dataInicioInstalacao ?  (
                        <HelperText text={errors?.dataInicioInstalacao?.message} />
                    ) : '' }
                </FormControl>


                <IndividualFileInput 
                    onChange={handleUploadCartaInicioInstalacao} 
                    id={inicioInstalacao?.Id || 0} 
                    label="Carta de início da instalação"
                    codigoDocumento={inicioInstalacao?.CodigoDocumento || 0}
                    register={register}
                    registerName="CartaInicioInstalacao"
                    temArquivoInformado={temArquivoInformado}
                    disabled={!isEditavel}
                    fnBaixarDocumento={realizarDownloadDocumento}
                /> 
            </div>
            <div className="homologacao_botoes">
                <BlueHoverButton disabled={!isEditavel} onClick={handleClickOpenDialog}>Informar início de instalação</BlueHoverButton>
                <BlueHoverButton onClick={() => setOpenPopup(false)}>Fechar</BlueHoverButton>
                <Dialog
                    open={openDialog}
                    onClose={handleCloseDialog}
                    aria-labelledby="alert-dialog-title"
                    aria-describedby="alert-dialog-description"
                >
                    <DialogTitle id="alert-dialog-title" sx={{ backgroundColor:"#232323", fontWeight: "bold" }}>
                        {"Confirmação"}
                    </DialogTitle>
                    <DialogContent sx={{ backgroundColor:"#232323" }}>
                        <DialogContentText id="alert-dialog-description" sx={{ color: "whitesmoke" }}>
                            Após informar o início de instalação, não será mais possível modificar
                            a data de início e o documento vinculado. Tem certeza que deseja continuar?

                        </DialogContentText>
                    </DialogContent>
                    <DialogActions sx={{ backgroundColor:"#232323"}}>
                        <BlueHoverButton onClick={handleCloseDialog}>Cancelar</BlueHoverButton>
                        <BlueHoverButton autoFocus onClick={informarInicioDeInstalacao}>
                            Continuar
                        </BlueHoverButton>
                    </DialogActions>
                </Dialog>
            </div>

            <Snackbar open={openSuccessWindow} anchorOrigin={{ vertical: 'top', horizontal: 'center' }} autoHideDuration={6000} onClose={handleCloseSuccess}> 
                    <Alert onClose={handleCloseSuccess} severity="success" sx={{ width: '100%' }}>
                        {successMessage}
                    </Alert>
            </Snackbar>
            <Snackbar open={openErrorWindow} anchorOrigin={{ vertical: 'top', horizontal: 'center' }} autoHideDuration={6000} onClose={handleCloseError}> 
                    <Alert onClose={handleCloseError} severity="error" sx={{ width: '100%' }}>
                        {errorMessage}
                    </Alert>
            </Snackbar>

            <Modal open={isLoading}>
                <Box style={modalStyle}>
                    <CircularProgress color="inherit" />
                </Box>
            </Modal>
        </div>
    )
}

export { InicioInstalacao };